/**
 * Longest edge we send. Vision models downsample beyond roughly this size
 * anyway, so anything larger costs upload bandwidth and buys no accuracy.
 */
const MAX_EDGE_PX = 1568;

/** Re-encode quality for the JPEG we produce. */
const JPEG_QUALITY = 0.85;

/**
 * Above this we always re-encode. Attachments are inlined into the chat request
 * as base64 (which inflates them by ~33%), so a phone photo straight from the
 * camera roll can push a single message past what the API will accept — the
 * request then fails as a bare "connection error" with no hint that the picture
 * was the problem. Reported from iOS, where photos are largest.
 */
const ALWAYS_RECOMPRESS_BYTES = 1024 * 1024;

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('image decode failed'));
    img.src = src;
  });

const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('read failed'));
    reader.readAsDataURL(blob);
  });

/**
 * Shrink an image attachment to a size the chat request can carry, returning a
 * data URL plus the media type that data URL is actually in.
 *
 * Animated GIFs are passed through untouched — drawing one to a canvas would
 * flatten it to a single frame. Any failure (an undecodable format, no canvas)
 * falls back to the original bytes, so this can only ever make an upload more
 * likely to succeed, never less.
 */
export async function compressImageBlob(
  blob: Blob,
  mediaType: string,
): Promise<{ url: string; mediaType: string }> {
  const type = mediaType || blob.type || 'image/jpeg';

  if (type === 'image/gif') {
    return { url: await blobToDataUrl(blob), mediaType: type };
  }

  let objectUrl: string | null = null;
  try {
    objectUrl = URL.createObjectURL(blob);
    const img = await loadImage(objectUrl);

    const longestEdge = Math.max(img.naturalWidth, img.naturalHeight);
    const needsResize = longestEdge > MAX_EDGE_PX;
    if (!needsResize && blob.size <= ALWAYS_RECOMPRESS_BYTES) {
      return { url: await blobToDataUrl(blob), mediaType: type };
    }

    const scale = needsResize ? MAX_EDGE_PX / longestEdge : 1;
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));

    const ctx = canvas.getContext('2d');
    if (!ctx) return { url: await blobToDataUrl(blob), mediaType: type };
    // White backfill: transparent PNG pixels would otherwise turn black once
    // flattened into JPEG.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    const dataUrl = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
    // A canvas that produced nothing usable (tainted, or an encoder the browser
    // silently declined) is not worth trusting over the original.
    if (!dataUrl.startsWith('data:image/jpeg')) {
      return { url: await blobToDataUrl(blob), mediaType: type };
    }
    return { url: dataUrl, mediaType: 'image/jpeg' };
  } catch {
    return { url: await blobToDataUrl(blob), mediaType: type };
  } finally {
    if (objectUrl) URL.revokeObjectURL(objectUrl);
  }
}
